import React, { FC } from 'react';
import { ButtonProps } from '@material-ui/core';
import { DateRange, DefinedRange } from 'materialui-daterange-picker';
import CustomDateRangePicker from 'src/features/timezone/components/CustomDateRangePicker';
import TimezoneSelector from 'src/features/timezone/components/TimezoneSelector';
import { useTimezone } from 'src/features/timezone/hooks/useTimezone';
import {
  CalendarDate,
  toPickerDate,
  toPlainDate,
  todayIn,
  zoneAbbreviation
} from 'src/features/timezone/utils/datetime';

interface ZonedDateRangePickerProps {
  startDate?: CalendarDate | null;
  endDate?: CalendarDate | null;
  onChange: (_start: CalendarDate, _end: CalendarDate) => void;
  buttonLabel?: string;
  fullWidth?: boolean;
  buttonProps?: Omit<ButtonProps, 'onClick' | 'className' | 'aria-describedby'>;
}

const presetRanges = (zone: string): DefinedRange[] => {
  const today = todayIn(zone);
  const preset = (label: string, start: CalendarDate): DefinedRange => ({
    label,
    startDate: toPickerDate(start),
    endDate: toPickerDate(today)
  });
  return [
    preset('Last Week', today.subtract({ weeks: 1 })),
    preset('Last Month', today.subtract({ months: 1 })),
    preset('Last 3 Months', today.subtract({ months: 3 })),
    preset('Last 6 Months', today.subtract({ months: 6 })),
    preset('Last 12 Months', today.subtract({ months: 12 })),
    preset('This Year', today.with({ month: 1, day: 1 }))
  ];
};

const ZonedDateRangePicker: FC<ZonedDateRangePickerProps> = ({
  startDate,
  endDate,
  onChange,
  buttonLabel,
  fullWidth,
  buttonProps
}) => {
  const { zone } = useTimezone();
  const handleChange = (range: DateRange) => {
    if (range.startDate && range.endDate) {
      onChange(toPlainDate(range.startDate), toPlainDate(range.endDate));
    }
  };
  return (
    <CustomDateRangePicker
      startDate={startDate ? toPickerDate(startDate) : null}
      endDate={endDate ? toPickerDate(endDate) : null}
      onDateChange={handleChange}
      buttonLabel={buttonLabel}
      fullWidth={fullWidth}
      buttonLabelSuffix={`· ${zoneAbbreviation(zone)}`}
      popoverActions={<TimezoneSelector />}
      buttonProps={buttonProps}
      datePickerProps={{ definedRanges: presetRanges(zone) }}
    />
  );
};

export default ZonedDateRangePicker;
